import { formatCurrencyString } from 'src/utils/currency';
import { LineItem } from './order';
import { Subscription, SubscriptionData } from './subscription';
import { FeatureBilling, FeatureBillingData } from './featureBilling';

export type PlanMemberAccessStatus =
  | 'invited'
  | 'accepted'
  | 'declined'
  | 'revoked';

export interface PlanMemberAccessData {
  id: string;
  email: string;
  ownerEmail: string;
  ownerName: string;
  membershipShortName: string;
  status: PlanMemberAccessStatus;
  invitedAt: { formatted: string };
  respondedAt?: { formatted: string };
  revokedAt?: { formatted: string };
}

export const SKU_CONFIG: Record<string, any> = {
  PRO_MEMBERSHIP_BASE_MONTHLY: {
    isAnnualContract: true,
    seatsSKU: 'PRO_MEMBERSHIP_BASE_ADD_SEATS',
    seatNameSingular: 'Headset',
    seatNamePlural: 'Headsets',
    fullSeatDescription: 'Access per Headset'
  },

  MEMBERSHIP_YEARLY: {
    isPersonal: true
  },
  MEMBERSHIP_MONTHLY: {
    isPersonal: true
  }
};

export type MembershipLevel = 'none' | 'member' | 'pro';

export interface MembershipProps {
  id: string;
  status: 'active' | 'inactive' | 'trialing';
  level: MembershipLevel;
  shortName?: string;
  canShareAccess?: boolean;
  planMembersMax?: number;
  planMembersActive?: number;
  planMembersActiveOrInvited?: number;
  planMemberAccess?: PlanMemberAccessData[];
  endDate: {
    formatted: string;
  };
  startDate: {
    formatted: string;
  };
  subscription: SubscriptionData;
  featureBilling: FeatureBillingData[];
}

export class Membership {
  id: string;
  level: MembershipLevel;
  status: 'active' | 'inactive' | 'trialing';
  shortName?: string;
  canShareAccess?: boolean;
  planMembersMax?: number;
  planMembersActive?: number;
  planMembersActiveOrInvited?: number;
  planMemberAccess?: PlanMemberAccessData[];
  endDate: Date;
  startDate: Date;
  subscription: Subscription;
  featureBilling: FeatureBilling[];

  constructor(props: MembershipProps) {
    this.id = props.id;
    this.level = props.level;
    this.shortName = props.shortName;
    this.canShareAccess = props.canShareAccess;
    this.planMembersMax = props.planMembersMax;
    this.planMembersActive = props.planMembersActive;
    this.planMembersActiveOrInvited = props.planMembersActiveOrInvited;
    this.planMemberAccess = props.planMemberAccess;
    this.endDate = new Date(props.endDate.formatted);
    this.startDate = new Date(props.startDate.formatted);
    this.status = props.status;
    this.subscription = new Subscription(props.subscription);
    this.featureBilling = props.featureBilling.map(
      (f) => new FeatureBilling(f)
    );
  }

  isProfesssional() {
    return this.subscription.isProfesssional();
  }

  isPersonal() {
    return this.subscription.isPersonal();
  }

  getPriceQuanty(lineItem?: LineItem): number {
    if (!lineItem?.priceId) {
      return lineItem?.quantity ?? 0;
    }

    const isPrimary = this.isPrimaryFeature(lineItem.priceId);

    const feature = isPrimary
      ? this.getPrimaryFeatureUsage(lineItem.priceId)
      : this.getSecondaryFeatureUsage(lineItem.priceId);

    if (!feature) {
      return lineItem?.quantity ?? 0;
    }
    if (feature.noLimitPeriodsRemaining > 0) {
      return 0;
    }
    if (isPrimary && this.subscription.cancelAtPeriodEnd) {
      return 0;
    }
    if (
      !isPrimary &&
      this.subscription.contracts.length > 0 &&
      feature.used < this.subscription.contracts[0].minUsage
    ) {
      return this.subscription.contracts[0].minUsage - feature.included;
    }
    return feature.billed;
  }

  getUnitAmount(lineItem?: LineItem): number {
    if (!lineItem?.priceId) {
      return lineItem?.unitAmount ?? 0;
    }

    const featurePrice = this.featureBilling.find(
      (f) => f.secondaryPriceId === lineItem.priceId
    );

    if (!featurePrice) {
      return lineItem?.unitAmount ?? 0;
    }

    if (featurePrice.pricePerUnit > 0) {
      return featurePrice.pricePerUnit;
    }

    return lineItem?.unitAmount ?? 0;
  }

  getFeatureBillingByPriceId(priceId?: string): FeatureBilling | null {
    if (!priceId || !this.featureBilling || this.featureBilling.length <= 0) {
      return null;
    }
    const featureBilling = this.featureBilling.find(
      (f) => f.priceId === priceId || f.secondaryPriceId === priceId
    );
    return featureBilling ?? null;
  }

  isPrimaryFeature(priceId?: string): boolean {
    if (!priceId || !this.featureBilling || this.featureBilling.length <= 0) {
      return false;
    }
    return this.featureBilling.some((f) => f.priceId === priceId);
  }

  isSecondaryFeature(priceId?: string): boolean {
    if (!priceId || !this.featureBilling || this.featureBilling.length <= 0) {
      return false;
    }
    return this.featureBilling.some((f) => f.secondaryPriceId === priceId);
  }

  isSecondaryFeatureAndType(
    type: 'PAY_BY_USAGE' | 'PAY_BY_SEAT',
    priceId?: string
  ): boolean {
    if (!priceId || !this.featureBilling || this.featureBilling.length <= 0) {
      return false;
    }
    return (
      this.featureBilling.find((f) => f.secondaryPriceId === priceId)?.type ===
      type
    );
  }

  getHeadsetAccess(): {
    currentPeriodValue: number;
    nextPeriodValue: number;
    additionalSeatUnitAmount: number;
    baseSeatUnitAmount: number;
    currency?: string;
    sku: string;
  } | null {
    if (!this.isProfesssional() || this.featureBilling.length <= 0) {
      return null;
    }
    // find feature billing for headset access SKU and return ammounts for UI display

    const featureBilling = this.featureBilling.find(
      (fb) =>
        fb.type === 'PAY_BY_SEAT' &&
        SKU_CONFIG[fb.sku]?.seatsSKU === fb.secondarySku
    );
    if (!featureBilling) {
      return null;
    }

    const seatLineItem = this.subscription.nextInvoiceLineItems.find((li) => {
      //synchronize with next invoice line item quantity
      if (li.priceId === featureBilling.secondaryPriceId) {
        return true;
      }
      return false;
    });

    const baseLineItem = this.subscription.nextInvoiceLineItems.find((li) => {
      //synchronize with next invoice line item quantity
      if (li.priceId === featureBilling.priceId) {
        return true;
      }
      return false;
    });

    return {
      currentPeriodValue: Math.max(
        featureBilling.currentPeriodValue || 0,
        featureBilling.allowMinimumSeats
      ),
      nextPeriodValue: featureBilling.nextPeriodValue || 0,
      baseSeatUnitAmount: baseLineItem?.unitAmount || 0,
      additionalSeatUnitAmount: seatLineItem?.unitAmount || 0,
      currency: seatLineItem?.currency ?? baseLineItem?.currency ?? 'usd',
      sku: featureBilling.secondarySku
    };
  }

  getPrimaryFeatureUsage(priceId: string): {
    included: number;
    used: number;
    remaining: number;
    billed: number;
    noLimitPeriodsRemaining: number;
  } | null {
    return (
      this.getFeatureBillingByPriceId(priceId)?.getPrimaryFeatureUsage() ?? null
    );
  }

  getSecondaryFeatureUsage(priceId: string): {
    included: number;
    used: number;
    remaining: number;
    billed: number;
    noLimitPeriodsRemaining: number;
    usageChargeDescription?: string;
  } | null {
    return (
      this.getFeatureBillingByPriceId(priceId)?.getSecondaryFeatureUsage() ??
      null
    );
  }

  buildLineItemDescription(lineItem: LineItem): string {
    return `${lineItem.description}`;
  }

  buildLineItemSubDescription(lineItem: LineItem): string {
    if (this.isPrimaryFeature(lineItem.priceId)) {
      const featureUsage = this.getPrimaryFeatureUsage(lineItem.priceId!);

      return (featureUsage?.noLimitPeriodsRemaining ?? 0) > 0
        ? `${formatCurrencyString({
            value: lineItem.unitAmount,
            currency: lineItem.currency.toUpperCase()
          })} pre-paid`
        : this.subscription.cancelAtPeriodEnd
          ? 'Canceled'
          : (lineItem.secondaryDescription ?? '');
    }

    return lineItem.secondaryDescription ?? '';
  }

  getNumIncluded(lineItem: LineItem): number {
    const usage = this.getSecondaryFeatureUsage(lineItem.priceId);
    if (!usage) {
      return 0;
    }
    return usage.included;
  }
  isActiveOrTrial(): boolean {
    return this.status !== 'inactive';
  }

  hasExpired(): boolean {
    return this.status !== 'inactive';
  }
}
